import { z } from 'zod';
import { type ContactRole, OPPORTUNITY_STAGES, type OpportunityStage } from '../constants';
import { NullableText, type Paginated, RequiredText } from './common';

export const CLOSED_OPPORTUNITY_STAGES: readonly OpportunityStage[] = ['won', 'lost'];

/** numeric(12,2) upper bound. Amounts are rounded to cents server-side. */
const MAX_AMOUNT = 9_999_999_999.99;
const Money = z.number({ error: 'Enter an amount' }).min(0, 'Must be 0 or more').max(MAX_AMOUNT, 'Amount is too large');
const Probability = z.number().int('Use a whole percentage').min(0, 'Between 0 and 100').max(100, 'Between 0 and 100');
const IsoDate = z.iso.date({ error: 'Use a date like 2026-10-31' });
const IsoDateTime = z.iso.datetime({ offset: true, error: 'Invalid date/time' });
const Version = z.number().int().min(1);

const csvOrArray = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess((v) => (typeof v === 'string' ? (v === '' ? [] : v.split(',')) : v), z.array(item));

/** One line item: a catalogue product (description defaults to its name) or a free-text item. */
export const OpportunityLineItemInput = z
  .object({
    productId: z.uuid().nullable().default(null),
    description: z.string().trim().max(300).default(''),
    quantity: z.number({ error: 'Enter a quantity' }).int('Whole units only').min(1, 'At least 1').max(100_000),
    unitPrice: Money,
  })
  .refine((item) => item.productId !== null || item.description !== '', {
    path: ['description'],
    message: 'Pick a product or describe the item',
  });
export type OpportunityLineItemInput = z.input<typeof OpportunityLineItemInput>;

const LineItems = z.array(OpportunityLineItemInput).max(100, 'At most 100 line items');

/** POST /api/leads/:id/opportunities — new deals start in "Qualifying". */
export const CreateOpportunityBody = z.object({
  title: RequiredText(200),
  products: LineItems.default([]),
  /** Overrides the line-item total when set. */
  amountOverride: Money.nullable().optional(),
  /** null → stage default from settings. */
  probability: Probability.nullable().optional(),
  expectedCloseDate: IsoDate.nullable().optional(),
  primaryContactId: z.uuid().nullable().optional(),
  competitors: NullableText(500).optional(),
  description: NullableText(5000).optional(),
  /** Defaults to the lead owner, else the current user. */
  ownerId: z.uuid().nullable().optional(),
});
export type CreateOpportunityBody = z.input<typeof CreateOpportunityBody>;

/** PATCH /api/opportunities/:id — optimistic locking via `version`; only present keys change. */
export const UpdateOpportunityBody = z.object({
  version: Version,
  title: RequiredText(200).optional(),
  amountOverride: Money.nullable().optional(),
  probability: Probability.nullable().optional(),
  expectedCloseDate: IsoDate.nullable().optional(),
  primaryContactId: z.uuid().nullable().optional(),
  competitors: NullableText(500).optional(),
  description: NullableText(5000).optional(),
  ownerId: z.uuid().nullable().optional(),
  quoteNumber: NullableText(60).optional(),
  quoteAmount: Money.nullable().optional(),
  quoteSentAt: IsoDateTime.nullable().optional(),
  quoteValidUntil: IsoDate.nullable().optional(),
  /** Only for lost opportunities (correcting the recorded reason). */
  lostReason: NullableText(100).optional(),
  lostReasonNotes: NullableText(2000).optional(),
});
export type UpdateOpportunityBody = z.input<typeof UpdateOpportunityBody>;

/** PUT /api/opportunities/:id/products — replaces every line item and recomputes the estimated value. */
export const ReplaceOpportunityProductsBody = z.object({
  version: Version,
  products: LineItems,
});
export type ReplaceOpportunityProductsBody = z.input<typeof ReplaceOpportunityProductsBody>;

/**
 * POST /api/opportunities/:id/stage — moves the deal and applies the lead-level effects (business stage,
 * relationship, timeline, next action). Moving to "lost" requires a reason.
 */
export const ChangeOpportunityStageBody = z
  .object({
    stage: z.enum(OPPORTUNITY_STAGES),
    /** When present, must match the current version (409 otherwise). */
    version: Version.optional(),
    lostReason: NullableText(100).optional(),
    lostReasonNotes: NullableText(2000).optional(),
    /** quote_sent: when the quote went out (default now; cannot be in the future). */
    quoteSentAt: IsoDateTime.optional(),
    quoteAmount: Money.nullable().optional(),
    quoteNumber: NullableText(60).optional(),
  })
  .superRefine((body, ctx) => {
    if (body.stage === 'lost' && !body.lostReason) {
      ctx.addIssue({ code: 'custom', path: ['lostReason'], message: 'Pick why this deal was lost' });
    }
  });
export type ChangeOpportunityStageBody = z.input<typeof ChangeOpportunityStageBody>;

export const OPPORTUNITY_CLOSING_WINDOWS = ['this_month', 'next_90_days', 'overdue'] as const;
export type OpportunityClosingWindow = (typeof OPPORTUNITY_CLOSING_WINDOWS)[number];
export const OPPORTUNITY_CLOSING_LABELS: Record<OpportunityClosingWindow, string> = {
  this_month: 'Closing this month',
  next_90_days: 'Closing in next 90 days',
  overdue: 'Past expected close',
};

export const OPPORTUNITY_SORT_FIELDS = [
  'expectedCloseDate',
  'value',
  'weighted',
  'stage',
  'business',
  'title',
  'daysInStage',
  'createdAt',
  'updatedAt',
] as const;
export type OpportunitySortField = (typeof OPPORTUNITY_SORT_FIELDS)[number];

/**
 * GET /api/opportunities. `stage`/`status` pick which deals are listed; `totals` ignore them (so a board can
 * show every column) but honour every other filter. `closedWithinDays` hides deals closed before the window.
 */
export const OpportunityListQuery = z.object({
  businessId: z.uuid().optional(),
  stage: csvOrArray(z.enum(OPPORTUNITY_STAGES)).optional(),
  status: z.enum(['open', 'closed', 'all']).default('all'),
  owner: z.union([z.literal('me'), z.literal('unassigned'), z.uuid()]).optional(),
  closing: z.enum(OPPORTUNITY_CLOSING_WINDOWS).optional(),
  closedWithinDays: z.coerce.number().int().min(1).max(3650).optional(),
  q: z.string().trim().max(200).optional(),
  sort: z.enum(OPPORTUNITY_SORT_FIELDS).default('expectedCloseDate'),
  dir: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(50),
});
export type OpportunityListQuery = z.input<typeof OpportunityListQuery>;

export type OpportunityProductDTO = {
  id: string;
  productId: string | null;
  categoryKey: string | null;
  description: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  sortOrder: number;
};

export type OpportunityContactDTO = {
  id: string;
  name: string;
  role: ContactRole;
  title: string | null;
  email: string | null;
  phone: string | null;
};

export type OpportunityDTO = {
  id: string;
  businessId: string;
  businessName: string;
  city: string | null;
  tierKey: string;
  title: string;
  stage: OpportunityStage;
  stageChangedAt: string;
  daysInStage: number;
  ownerId: string | null;
  primaryContactId: string | null;
  primaryContact: OpportunityContactDTO | null;
  products: OpportunityProductDTO[];
  /** Σ(quantity × unitPrice) of the line items. */
  lineItemsTotal: number;
  amountOverride: number | null;
  /** amountOverride ?? lineItemsTotal. */
  estimatedValue: number;
  probability: number | null;
  /** probability ?? stage default from settings. */
  effectiveProbability: number;
  weightedValue: number;
  currency: string;
  expectedCloseDate: string | null;
  quoteNumber: string | null;
  quoteAmount: number | null;
  quoteRequestedAt: string | null;
  quoteSentAt: string | null;
  quoteValidUntil: string | null;
  competitors: string | null;
  lostReason: string | null;
  lostReasonNotes: string | null;
  wonAt: string | null;
  lostAt: string | null;
  closedBy: string | null;
  description: string | null;
  version: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** What the current user may do (the API enforces the same rules). */
  access: { canEdit: boolean; canDelete: boolean; canReopen: boolean };
};

export type OpportunityStageTotals = { count: number; value: number; weighted: number };

export type OpportunityTotalsDTO = {
  byStage: Record<OpportunityStage, OpportunityStageTotals>;
  openCount: number;
  openValue: number;
  weightedValue: number;
  /** Value of deals won in the last 90 days. */
  wonValue90d: number;
};

export type OpportunityListResponse = Paginated<OpportunityDTO> & { totals: OpportunityTotalsDTO };
